import React, { useState, useEffect, useRef } from 'react';
import { User, Search, ChevronDown, Check } from 'lucide-react';
import { api } from '../../../utils/api';
import { toast } from 'react-hot-toast';
import './UserSelector.css';

const UserSelector = ({ value, onChange, disabled = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/webform/api-keys/users');
      setUsers(response);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = users.find(u => u.userId === parseInt(value));

  const filteredUsers = users.filter(user =>
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId.toString().includes(searchTerm)
  );

  const handleSelect = (user) => {
    onChange(user.userId.toString());
    setIsOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="user-selector loading">
        <User size={16} />
        <span>Loading users...</span>
      </div>
    );
  }

  return (
    <div className="user-selector-container" ref={dropdownRef}>
      <div 
        className={`user-selector ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <User size={16} className="selector-icon" />
        <div className="selector-value">
          {selectedUser ? (
            <div className="selected-user">
              <span className="user-name">{selectedUser.displayName}</span>
              <span className="user-id">ID: {selectedUser.userId}</span>
            </div>
          ) : (
            <span className="placeholder">Select a user...</span>
          )}
        </div>
        <ChevronDown size={18} className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </div>

      {isOpen && (
        <div className="user-dropdown">
          <div className="dropdown-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>

          <div className="dropdown-list">
            {filteredUsers.length === 0 ? (
              <div className="no-results">
                <span>No users found</span>
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.userId}
                  className={`user-option ${selectedUser?.userId === user.userId ? 'selected' : ''}`}
                  onClick={() => handleSelect(user)}
                >
                  <User size={16} />
                  <div className="user-info">
                    <span className="user-name">{user.displayName}</span>
                    {user.email && (
                      <span className="user-email">{user.email}</span>
                    )}
                  </div>
                  <span className="user-id">ID: {user.userId}</span>
                  {selectedUser?.userId === user.userId && (
                    <Check size={16} className="check-icon" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSelector;
